import { useEffect, useState } from 'react';
import { Portal } from '@aviv-estimation-funnel/estimation-data-models/enums';
import { CSSObject, Global } from '@emotion/react';
import { Box, useTokens } from '@gemini/core';
import { Autocomplete } from '@gemini/ui';
import { addError } from '@mfe-helpers/datadog';
import { debouncePromise } from '@mfe-helpers/debouncePromise';
import { fetchAutocompleteAPI } from '@mfe-helpers/location/common';
import { useAppContext } from '@mfe-presentation/contexts';
import type { IFieldProps } from '@mfe-types/field';
import { AutocompleteData } from '@mfe-types/geo';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocale, useTranslation } from '@aviv-unified-frontend/runtime/i18n';
import { NAME_SPACE } from '@mfe-presentation/hooks/useLoadTranslations';
import type { Tokens } from '@gemini/tokens';

const itemsStyles = (tokens: Tokens): CSSObject => ({
    // Options selector
    'ul[role="listbox"] li[role="option"]': {
        justifyContent: 'start',
        display: 'inline',
        'div:nth-of-type(2)': {
            marginLeft: 0,
        },
        div: {
            display: 'inline',
        },
    },
    // Loading selector
    'div[role="listbox"] li:not([role="option"])': {
        padding: 0,
        'div[role="status"]': {
            lineHeight: tokens['spacing.16'],
            fontSize: tokens['spacing.16'],
        },
    },
    // No results selector
    'div[role="listbox"] div[role="option"]': {
        div: {
            marginBottom: 0,
        },
    },
});

const StreetAutocomplete = ({ field, texts, styles }: IFieldProps<any, any, any>) => {
    const { portal } = useAppContext();
    const [displayAutocompleteError, setDisplayAutocompleteError] = useState(false);
    const [dataListOptions, setDataListOptions] = useState<Array<AutocompleteData>>([]);
    const [fetchingPlaces, setFetchingPlaces] = useState(false);
    const [selectedStreet, setSelectedStreet] = useState<AutocompleteData | undefined>();
    const { control, setValue } = useFormContext();
    const { t } = useTranslation(NAME_SPACE);
    const tokens = useTokens();
    const { locale } = useLocale();

    const streetName = useWatch({ control, name: 'streetName' });
    const streetDisplayName = useWatch({ control, name: 'streetDisplayName' });
    const cityId = useWatch({ control, name: 'cityId' });
    const postalCode = useWatch({ control, name: 'postalCode' });
    const streetId = useWatch({ control, name: 'streetId' });

    const isGermanPortal = portal === Portal.ImmoWelt || portal === Portal.Immonet;
    const { onChange, autoComplete, ...fieldProps } = field;

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        if (!field.isDisabled) {
            // workaround to force the display of the error label if errors are shown for the current step but the street autocomplete is disabled
            timeoutId = setTimeout(() => {
                setDisplayAutocompleteError(true);
            }, 150);
        } else {
            setDisplayAutocompleteError(false);
        }
        return () => clearTimeout(timeoutId);
    }, [field.isDisabled]);

    const fetchStreets = async (value: string, callback: () => void) => {
        const results = await fetchAutocompleteAPI({
            text: value,
            portal,
            addressLevel: 'street',
            parentId: cityId,
            isGermanPortal,
            limit: 10,
            locale,
        });
        callback();
        return results;
    };

    const onStreetChange = async (inputValue: string) => {
        setFetchingPlaces(true);
        setDataListOptions([]);
        try {
            if (inputValue !== streetName) {
                field.onChange(inputValue);
                if (inputValue) {
                    const results: Array<AutocompleteData> = await debouncePromise<
                        Array<AutocompleteData>
                    >(async () => fetchStreets(inputValue, () => setFetchingPlaces(false)), 300);

                    setDataListOptions(results);
                } else setFetchingPlaces(false);
            }
        } catch (e) {
            addError(e, { payload: inputValue });
        }
    };

    const onSubmit = (_value: string | null, key: string | null) => {
        if (key) {
            const item = dataListOptions.find((item) => item.id === key);
            if (item) {
                setValue('streetName', item.name);
                setValue('streetId', item.id);
                setValue('streetDisplayName', item.displayName);
                if (item.postalCode !== postalCode) {
                    setValue('postalCode', item.postalCode);
                }
                setSelectedStreet(item);
            }
        }
    };

    useEffect(() => {
        if (!streetName) {
            setValue('streetId', '');
            setValue('houseNumber', '');
            setValue('addressId', '');
            setValue('streetDisplayName', '');
            setSelectedStreet(undefined);
        }
        // For handover case, fill in the list options
        if (streetName && streetId && postalCode && streetDisplayName && !selectedStreet) {
            setSelectedStreet({
                id: streetId,
                name: streetName,
                displayName: streetDisplayName,
                postalCode,
            });
        }
    }, [streetName]);

    useEffect(() => {
        /* Prevent from having the dataListOptions to be filled in with last call when the user
        is cleaning the street field with return button character by character */
        if (dataListOptions?.length && !fieldProps.value) {
            setDataListOptions([]);
        }
    }, [dataListOptions]);

    useEffect(() => {
        // When the user selects a street name, only that selection remains in the options list
        const isEmpty = !dataListOptions?.length;
        const hasSeveralOptions = dataListOptions?.length > 1;
        const hasADifferentOption = dataListOptions?.[0] !== selectedStreet;

        if (isEmpty || hasSeveralOptions || hasADifferentOption) {
            setDataListOptions(selectedStreet ? [selectedStreet] : []);
        }
    }, [selectedStreet]);

    useEffect(() => {
        // For a11y purpose, remove non-standard attributes
        const streetNameElem = document.getElementsByName('streetName')[0];
        if (streetNameElem) {
            streetNameElem.removeAttribute('autocorrect');
            // autocomplete value is not passed to the input element, so we need to set it manually.
            // TODO: Gemini team will eventually come up with a solution for this
            // https://avivgroup.atlassian.net/browse/GEMINI-1414
            streetNameElem.setAttribute('autocomplete', autoComplete ?? 'off');
        }
    }, []);

    const autocompleteItems = dataListOptions.map((option) => ({
        id: option.id,
        label: option.name,
        suffix: option.displayName.replace(option.name, ''),
    }));

    return (
        <Box {...styles}>
            <Global styles={itemsStyles(tokens)} />
            <Autocomplete
                {...fieldProps}
                label={texts.label ?? ''}
                items={autocompleteItems}
                onChange={onStreetChange}
                onSubmit={onSubmit}
                emptyState={
                    field.value && !autocompleteItems.length
                        ? {
                              title: t('common.fields.streetName.noResults'),
                              description: '',
                          }
                        : undefined
                }
                isLoading={fetchingPlaces}
                error={displayAutocompleteError ? field.error : undefined}
                testId="search-wrapper"
                shouldFocusFirstItem
                isFullWidth
            />
        </Box>
    );
};

export default StreetAutocomplete;
